import dayjs from "dayjs";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DownloadIcon } from "../../assets/icons";
import BackArrow from "../../assets/icons/backArrow";
import DeleteIcon from "../../assets/icons/DeleteIcon";
import EditIcon from "../../assets/icons/EditIcon";
import { GroupButton, SidebarDrawer, Table } from "../../components/common";
import PageLoader from "../../components/common/PageLoader";
import { ExpenseDeletePrompt } from "../../components/expense/ExpenseDelete";
import { formatMoney } from "../../utils";
import {
  convertExpense,
  editToggleProtected,
  getExpense,
} from "../../backend-services";

import LeftNotificationPopup from "../../components/core/NotificationPopup/LeftNotificationPopup";
import ProcessSuccessful from "../../assets/icons/ProcessSuccessful";
import { useExpense } from "../../hooks/expenses/useExpense";
import { MobileTable } from "../../components/common/mobileTable";
import { useUserStore } from "../../state";
import AnnotationComponent from "../../components/common/Annotation/AnnotationComponent";
import useAnnotations from "../../hooks/annotation/useAnnotations";
import AttachIcon from "../../assets/icons/AttachIcon";
import { UploadExpenseReceipt } from "../../components/expense/UploadReceipt";
import { isNotAccountant } from "../../utils/isNotAccountant";
import ProtectedExpenseModal from "../../components/expense/ExpenseCreate/ProtectedExpenseModal";
import { ExpenseDetails } from "../../components/expense/expenseDetails";
import AddSquare from "../../assets/icons/AddSquare";
import { DotMenu } from "../../assets/icons/DotMenu";
import { useTranslation } from "react-i18next";
import NewAttachIcon from "../../assets/icons/NewAttach";
import NewEditIcon from "../../assets/icons/NewEdit";
import NewDeleteIcon from "../../assets/icons/NewDelete";
import NewDownloadIcon from "../../assets/icons/NewDownloadIcons";
import { showToast } from "../../utils/toastUtils";
import PlusIcon from "../../assets/icons/PlusIcon";
import PlusIconLight from "../../assets/icons/PlusIconLight";
import { useProjectHook } from "../../hooks/project/useProject";
import CancelBrokenIcon from "../../assets/icons/CancelBrokenIcon";
import ExpenseMenu from "./expenseMenu";

interface IGroupButton {
  text: string;
  icon: ReactNode;
  textColorClass?: string;
  onClick?: () => void;
  href?: string;
}

interface GroupButtonProps {
  buttons: IGroupButton[];
}

const ViewExpense = () => {
  const { t } = useTranslation("expenses");
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [showPrompt, setShowPrompt] = React.useState(false);
  const [showSuccessModal, setShowSuccessModal] = React.useState(false);
  const [showSuccessfulModal, setShowSuccessfulModal] = React.useState(false);
  const [invoices, setInvoices] = React.useState<any[]>([]);
  const [isBillable, setIsBillable] = React.useState(false);
  const [isProtected, setIsProtected] = React.useState(false);
  const [downloading, setDownloading] = React.useState(false);
  const [showUploadReceipt, setShowUploadReceipt] = React.useState(false);
  const { roles } = useUserStore();
  const [openDotMenu, setOpenDotMenu] = React.useState(false);

  const { expenseId } = useParams<{ expenseId: any }>();
  const { expense, isLoading, annotations, fetchExpense } =
    useExpense(expenseId);
    const nav = useNavigate();
  const {
    selectedProject,
    RemoveProjectButton,
    SelectProjectModal,
    openSelectModal,
    CreateProjectSideBar,
    RemoveProjectModal,
    openRemovalModal,
  } = useProjectHook({ expenseId, projectId: expense?.projectId });

  const isAdmin = roles.includes("Admin");

  const params = useParams();

  if (!expenseId) {
    throw new Error("Expense Id is missing");
  }

  const [isConverting, setIsConverting] = React.useState(false);
  const [isConverted, setIsConverted] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const annotationsData = useAnnotations(annotations, "Expense");

  useEffect(() => {
    if (expense) {
      setIsProtected(expense.isProtected);
    }
  }, [expense]);

  const fetchExpenseDetails = async () => {
    if (!expenseId) return;
    try {
      fetchExpense();
      const updatedExpense = await getExpense(expenseId);
      if (updatedExpense) {
        setIsProtected(updatedExpense.isProtected);
      }
    } catch (error) {
      console.error("Error fetching updated expense details:", error);
    }
  };

  const handleDownloadClick = useCallback(async () => {
    setDownloading(true);
    try {
      if (!expenseId) throw new Error("Expense Id is missing");
      const expense = await getExpense(expenseId);
      if (expense && expense.receiptUrl) {
        const receiptUrl = expense.receiptUrl;
        const formattedDate = dayjs(expense.date).format("YYYY-MM-DD");
        const fileName = `receipt_${expenseId}_${formattedDate}.pdf`;
        window.location.href = receiptUrl;
      }
    } catch (error) {
      setError("Error fetching expense. Please try again later.");
    } finally {
      setDownloading(false);
    }
  }, [expenseId]);

  const data = useMemo(() => {
    return (
      expense?.lineItems.map(
        (exp: { name: any; quantity: string; rate: string }, i: number) => ({
          id: i + 1,
          name: exp?.name,
          quantity: exp?.quantity,
          rate: formatMoney(`${exp?.rate}`),
          amount: formatMoney(`${+exp.rate * +exp.quantity}`),
        }),
      ) || []
    );
  }, [expense]);

  const renderLineItemsData = () => {
    return data;
  };

  const renderLineItemsTableHead = () => {
    return {
      id: { title: "#" },
      name: { title: "Items/Description" },
      quantity: { title: "Quantity" },
      rate: { title: "Rate" },
      amount: { title: "Amount" },
    };
  };

  const columns = [
    { key: "id", label: "#" },
    { key: "name", label: "Items/Description" },
    { key: "quantity", label: "Quantity" },
    { key: "rate", label: "Rate" },
    { key: "amount", label: "Amount" },
  ];

  const handleExpenseDeleteSuccess = () => {
    setShowSuccessModal(true);
  };

  const handleConvertExpense = async (id: string) => {
    setIsConverting(true);
    try {
      const createdInvoice = await convertExpense(expenseId);
      setInvoices((prevInvoices) => [...prevInvoices, createdInvoice]);
      setIsConverting(false);
      setIsConverted(true);
      setShowSuccessfulModal(true);
      setTimeout(() => {
        navigate("/invoices");
      }, 2000);
    } catch (error: any) {
      setErrorMessage("Unable to Convert Expense.");
      setIsConverting(false);
    }
  };

  const handleProtectedExpenseModal = () => {
    setIsOpenModal(true);
  };

  const handleIsProtectedChange = async () => {
    try {
      await editToggleProtected(expenseId, { isProtected: !isProtected });
      setIsOpenModal(false);
      setIsProtected((prevIsProtected) => !prevIsProtected);
      showToast("Expense protection status updated successfully.", "success");
    } catch (error) {
      showToast(
        "Failed to update expense protection status. Please try again later.",
        "error",
      );
      setErrorMessage("Error updating expense protection status:");
    }
  };

  const groupButtonProps = useMemo(
    () => ({
      buttons: [
        {
          text: !expense?.projectId ? "Add Project" : "Remove Project",
          icon: !expense?.projectId ? <PlusIconLight /> : <CancelBrokenIcon />,
          onClick: !expense?.projectId ? openSelectModal : openRemovalModal,
          textColorClass: "text-[#344054]",
          isVisible: true,
        },
        {
          text: "Edit",
          icon: <NewEditIcon />,
          href: `/expenses/edit/${expenseId}`,
          textColorClass: "text-[#344054]",
          isVisible: true,
        },
        {
          text: `Attach Receipt`,
          icon: <NewAttachIcon />,
          onClick: () => setShowUploadReceipt(true),
          textColorClass: "text-[#344054]",
          isVisible: true,
        },
        {
          text: downloading ? "Downloading..." : "Download",
          icon: <NewDownloadIcon color="black" />,
          onClick: handleDownloadClick,
          textColorClass: "text-[#344054]",
          isVisible: !!expense?.receiptUrl,
        },
        {
          text: "Delete",
          icon: <NewDeleteIcon />,
          onClick: () => setShowPrompt(true),
          textColorClass: "text-[#D92D20]",
          fontSize: "text-[#D92D20]",
          isVisible: true,
        },
      ]
        .filter((i) => i.isVisible)
        .filter(Boolean),
    }),
    [downloading, expenseId, handleDownloadClick, expense],
  );
  const MobileGroupButtonProps = useMemo(
    () => ({
      buttons: [
        {
          text: !expense?.projectId ? "Add Project" : "Remove Project",
          icon: !expense?.projectId ? <PlusIconLight /> : <CancelBrokenIcon />,
          onClick: !expense?.projectId ? openSelectModal : openRemovalModal,
          textColorClass: "text-[#344054]",
          isVisible: true,
        },
        {
          text: "Edit",
          icon: <NewEditIcon />,
          href: `/expenses/edit/${expenseId}`,
          fontSize: "text-[14px]",
        },
        {
          text: `Attach Receipt`,
          icon: <NewAttachIcon />,
          onClick: () => setShowUploadReceipt(true),
          textColorClass: "text-[#044E97]",
          fontSize: "text-[14px]",
        },
        {
          text: "Delete",
          icon: <NewDeleteIcon />,
          onClick: () => setShowPrompt(true),
          textColorClass: "text-red-600",
          fontSize: "text-red-600",
        },
      ].filter(Boolean),
    }),
    [downloading, expenseId, handleDownloadClick, expense],
  );

  return (
    <>
      <div className="px-1 540:px-3 md:px-[30px] lg:px-0 ">
        {isLoading ? (
          <PageLoader></PageLoader>
        ) : (
          <div className="lg:mt-8 pb-4">
            <div className="flex md:justify-between items-center">
              <div>
                <span
                  className="flex cursor-pointer items-center gap-[10.75px] md:gap-3"
                  onClick={() => nav(-1)}
                >
                  <BackArrow />
                  <h1 className="font-semibold text-[20px] ">View expense</h1>
                </span>
              </div>
            </div>

            <div className="mt-4 hidd">
              <div className="w-full flex gap-3 justify-end"></div>
            </div>

            <div className="md:mx-[50px] mt-10 mb-3 md:flex justify-between items-center gap-4 md:mb-0 hidden ">
              <h1 className="text-xl font-semibold capitalize">
                {expense.name}
              </h1>
              <div className="md:flex md:gap-4 md:items-center flex">
                <div className="hidden text-gray-700 md:flex gap-2 items-center">
                  {isNotAccountant(roles) && (
                    <ExpenseMenu buttons={groupButtonProps.buttons} />
                  )}
                  <AnnotationComponent
                    type="expense"
                    annotationData={annotationsData}
                  />
                </div>
              </div>
            </div>

            <div className=" md:hidden py-6">
              <div className="flex justify-between md:hidden">
                <h1 className="text-2xl">{expense.name}</h1>
                <div className="flex gap-3 items-center">
                  {isNotAccountant(roles) && (
                    <ExpenseMenu buttons={groupButtonProps.buttons} />
                  )}
                  <AnnotationComponent
                    type="expense"
                    annotationData={annotationsData}
                  />
                </div>
              </div>
            </div>

            <div className="w-full mt- md:mt-[32px]  md:px-[50px]">
              <ExpenseDetails
                expense={expense}
                isBillable={isBillable}
                isProtected={isProtected}
                handleIsProtectedChange={handleProtectedExpenseModal}
                handleDownloadClick={handleDownloadClick}
                downloading={downloading}
                expenseId={expenseId} // Pass expenseId
                setShowUploadReceipt={setShowUploadReceipt} // Pass setShowUploadReceipt
                setShowPrompt={setShowPrompt} // Pass setShowPrompt
              />
            </div>

            <div className="mt-[1.5em] md:mt-[3em] md:px-12 px-2">
              <h3 className="text-[#00000] text-[16px] lg:text-[20px] mb-[-12px] font-semibold leading-6 ">
                {t("budget.expenseHistory")}
              </h3>
              <div className="md:hidden">
                <MobileTable
                  expenseCard={true}
                  searchPlaceHolder="Search"
                  searchDisplayKey="id"
                  searchResult={[]}
                  head={renderLineItemsTableHead()}
                  data={renderLineItemsData()}
                  isLoading={isLoading}
                  buttons={[]}
                  fetchMoreData={undefined}
                  meta={undefined}
                />
              </div>
              <div className="hidden md:block">
                <Table
                  columns={columns}
                  expenseCard={true}
                  searchPlaceHolder="Search"
                  searchDisplayKey="id"
                  searchResult={[]}
                  head={renderLineItemsTableHead()}
                  data={renderLineItemsData()}
                  isLoading={isLoading}
                  buttons={[]}
                  fetchMoreData={undefined}
                  meta={undefined}
                />
              </div>
            </div>

            {showSuccessfulModal && (
              <>
                <SidebarDrawer
                  title={"Invoice Created"}
                  show={true}
                  position={"left"}
                  onClose={() => setShowSuccessfulModal(false)}
                >
                  <LeftNotificationPopup
                    title={""}
                    icon={<ProcessSuccessful />}
                    onClose={() => setShowSuccessfulModal(false)}
                  />
                </SidebarDrawer>
              </>
            )}
          </div>
        )}
        {showUploadReceipt && (
          <UploadExpenseReceipt
            expenseId={expenseId}
            closeModal={setShowUploadReceipt}
            onReceiptUploaded={fetchExpenseDetails} // Refresh details after upload
          />
        )}
        {isOpenModal && (
          <ProtectedExpenseModal
            setIsOpenModal={setIsOpenModal}
            handleProtectedChange={handleIsProtectedChange}
            isProtected={isProtected} // Add this line
          />
        )}
        {showPrompt && (
          <div className="w-full">
            <ExpenseDeletePrompt
              showPrompt={showPrompt}
              expenseId={expenseId}
              onClose={() => setShowPrompt(false)}
              onDeleteSuccess={handleExpenseDeleteSuccess}
            />
          </div>
        )}
      </div>
      <SelectProjectModal />
      <CreateProjectSideBar />
      <RemoveProjectModal
        id={expenseId}
        name={selectedProject?.name!}
        onRefetch={fetchExpense}
      />
    </>
  );
};

export { ViewExpense };
